import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ConfirmationModal from '../../components/ConfirmationModal';
import ALL_INTEGRATIONS_QUERY from '../../graphql/allIntegrations';
import { gql } from '../../graphql/generated/gql';
import { INSTALLABLE_PLATFORM_INFO_MAP } from '../../integrations/IntegrationPlatformInfo';
import { UIActions, UIActions as UIActionsNew } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';

const DELETE_INTEGRATION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteIntegration($platform: String!) {
    deleteIntegration(platform: $platform) {
      id
    }
  }
`);

const useDeleteIntegration = () => {
  const dispatch = useAppDispatch();
  const [mutate, result] = useMutation(DELETE_INTEGRATION_MUTATION, {
    update: legacyUpdateQueryList(
      { query: ALL_INTEGRATIONS_QUERY },
      'session.organization.integrations',
      'deleteIntegration',
      false,
    ),
  });

  const deleteIntegration = useCallback(
    (platform: string) => {
      const platformInfo = INSTALLABLE_PLATFORM_INFO_MAP[platform];
      if (!platformInfo) {
        return;
      }
      const { name } = platformInfo;

      dispatch(
        UIActionsNew.openModal(ConfirmationModal, {
          title: 'Remove Integration?',
          message: `Are you sure you want to remove the integration with ${name}?`,
          onConfirm: async () => {
            await mutate({ variables: { platform } });
            dispatch(
              UIActions.pushToast({
                level: 'success',
                message: `${name} integration removed`,
              }),
            );
          },
        }),
      );
    },
    [dispatch, mutate],
  );

  return [deleteIntegration, result] as const;
};

export default useDeleteIntegration;
